import { Component, ComponentType } from "@/types";
import { BaseCommandHandler, CommandResult, PropertyChange } from "../BaseCommandHandler";

type IconPosition = 'left' | 'right' | 'top';

const isIconPosition = (value: string): value is IconPosition =>
  value === 'left' || value === 'right' || value === 'top';

/**
 * Command Handler for DataCard components with DataText integration.
 *
 * Handles commands for:
 * - Data binding mode (enable/disable data text)
 * - Value and label field configuration
 * - Typography and styling
 * - Loading/error state configuration
 *
 * Created: January 2026 (Phase 8: DataCard Integration with DataText)
 */
export class DataCardCommandHandler extends BaseCommandHandler {
  constructor() {
    super();
    this.initializePatterns();
  }

  private initializePatterns(): void {
    // ═══════════════════════════════════════════════════════════════════════════
    // DATA TEXT INTEGRATION COMMANDS
    // ═══════════════════════════════════════════════════════════════════════════
    this.commandPatterns.set(
      /enable data ?text(?: mode)?/i,
      () => ({ properties: { useDataText: true } })
    );

    this.commandPatterns.set(
      /disable data ?text(?: mode)?/i,
      () => ({ properties: { useDataText: false } })
    );

    this.commandPatterns.set(
      /(?:use |enable )?data ?binding/i,
      () => ({ properties: { useDataText: true } })
    );

    this.commandPatterns.set(
      /(?:use |enable )?static (?:value|mode)/i,
      () => ({ properties: { useDataText: false } })
    );

    this.commandPatterns.set(
      /(?:set |change )?value ?field (?:to )?["']?([a-zA-Z0-9_]+)["']?/i,
      (match) => ({ properties: { valueField: match[1] } })
    );

    this.commandPatterns.set(
      /(?:set |change )?label ?field (?:to )?["']?([a-zA-Z0-9_]+)["']?/i,
      (match) => ({ properties: { labelField: match[1], useDynamicLabel: true } })
    );

    this.commandPatterns.set(
      /enable dynamic label/i,
      () => ({ properties: { useDynamicLabel: true } })
    );

    this.commandPatterns.set(
      /disable dynamic label/i,
      () => ({ properties: { useDynamicLabel: false } })
    );

    this.commandPatterns.set(
      /(?:set |change )?empty ?(?:value )?text (?:to )?["']([^"']+)["']/i,
      (match) => ({ properties: { emptyValueText: match[1] } })
    );

    this.commandPatterns.set(
      /show loading(?: state)?/i,
      () => ({ properties: { showLoadingState: true } })
    );

    this.commandPatterns.set(
      /hide loading(?: state)?/i,
      () => ({ properties: { showLoadingState: false } })
    );

    this.commandPatterns.set(
      /show error(?: state)?/i,
      () => ({ properties: { showErrorState: true } })
    );

    this.commandPatterns.set(
      /hide error(?: state)?/i,
      () => ({ properties: { showErrorState: false } })
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // STATIC VALUE/LABEL COMMANDS
    // ═══════════════════════════════════════════════════════════════════════════
    this.commandPatterns.set(
      /(?:set |change )?value (?:to )?["']([^"']+)["']/i,
      (match) => ({ properties: { value: match[1] } })
    );

    this.commandPatterns.set(
      /(?:set |change )?value (?:to )?(\d+(?:\.\d+)?)/i,
      (match) => ({ properties: { value: parseFloat(match[1]) } })
    );

    this.commandPatterns.set(
      /(?:set |change )?label (?:to )?["']([^"']+)["']/i,
      (match) => ({ properties: { label: match[1] } })
    );

    this.commandPatterns.set(
      /(?:set |change )?subtitle (?:to )?["']([^"']+)["']/i,
      (match) => ({ properties: { subtitle: match[1] } })
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // VALUE FORMATTING COMMANDS
    // ═══════════════════════════════════════════════════════════════════════════
    this.commandPatterns.set(
      /format (?:as |like )?(currency|money)/i,
      () => ({ properties: { valueFormatter: 'currency' } })
    );

    this.commandPatterns.set(
      /format (?:as |like )?(percent|percentage)/i,
      () => ({ properties: { valueFormatter: 'percent' } })
    );

    this.commandPatterns.set(
      /format (?:as |like )?(number|numeric)/i,
      () => ({ properties: { valueFormatter: 'number' } })
    );

    this.commandPatterns.set(
      /format (?:as |like )?(compact|abbreviated|short)/i,
      () => ({ properties: { valueFormatter: 'compact' } })
    );

    this.commandPatterns.set(
      /format (?:as |like )?(integer|whole number)/i,
      () => ({ properties: { valueFormatter: 'integer' } })
    );

    this.commandPatterns.set(
      /format (?:as |like )?(date ?time|datetime)/i,
      () => ({ properties: { valueFormatter: 'datetime' } })
    );

    this.commandPatterns.set(
      /format (?:as |like )?(date)/i,
      () => ({ properties: { valueFormatter: 'date' } })
    );

    this.commandPatterns.set(
      /format (?:as |like )?(time)/i,
      () => ({ properties: { valueFormatter: 'time' } })
    );

    this.commandPatterns.set(
      /(?:show |set |use )?(\d+) decimals?/i,
      (match) => ({ properties: { decimalPlaces: parseInt(match[1], 10) } })
    );

    this.commandPatterns.set(
      /(?:set |change )?decimal places (?:to )?(\d+)/i,
      (match) => ({ properties: { decimalPlaces: parseInt(match[1], 10) } })
    );

    this.commandPatterns.set(
      /(?:remove|hide)\s+thousands? separator/i,
      () => ({ properties: { thousandsSeparator: false } })
    );

    this.commandPatterns.set(
      /(?:add |show |use )?thousands? separator/i,
      () => ({ properties: { thousandsSeparator: true } })
    );

    this.commandPatterns.set(
      /(?:set |change )?currency symbol (?:to )?["']?([^"'\s]+)["']?/i,
      (match) => ({ properties: { currencySymbol: match[1], valueFormatter: 'currency' } })
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // TYPOGRAPHY COMMANDS - VALUE
    // ═══════════════════════════════════════════════════════════════════════════
    this.commandPatterns.set(
      /(?:set |change )?value (?:color|colour) (?:to )?(.+)/i,
      (match) => {
        const colorValue = this.parseColorValue(match[1]);
        return colorValue ? { properties: { valueColor: colorValue } } : {};
      }
    );

    this.commandPatterns.set(
      /(?:set |change )?value (?:font )?size (?:to )?(.+)/i,
      (match) => ({ properties: { valueFontSize: match[1].trim() } })
    );

    this.commandPatterns.set(
      /(?:make )?value (bold|bolder)/i,
      () => ({ properties: { valueFontWeight: 700 } })
    );

    this.commandPatterns.set(
      /(?:make )?value (normal|regular)/i,
      () => ({ properties: { valueFontWeight: 400 } })
    );

    this.commandPatterns.set(
      /(?:set |change )?value (?:font )?weight (?:to )?(\d+)/i,
      (match) => {
        const weight = parseInt(match[1], 10);
        return { properties: { valueFontWeight: Math.min(900, Math.max(100, weight)) } };
      }
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // TYPOGRAPHY COMMANDS - LABEL
    // ═══════════════════════════════════════════════════════════════════════════
    this.commandPatterns.set(
      /(?:set |change )?label (?:color|colour) (?:to )?(.+)/i,
      (match) => {
        const colorValue = this.parseColorValue(match[1]);
        return colorValue ? { properties: { labelColor: colorValue } } : {};
      }
    );

    this.commandPatterns.set(
      /(?:set |change )?label (?:font )?size (?:to )?(.+)/i,
      (match) => ({ properties: { labelFontSize: match[1].trim() } })
    );

    this.commandPatterns.set(
      /(?:make )?label (bold|bolder)/i,
      () => ({ properties: { labelFontWeight: 700 } })
    );

    this.commandPatterns.set(
      /(?:make )?label (normal|regular)/i,
      () => ({ properties: { labelFontWeight: 400 } })
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // CARD STYLING COMMANDS
    // ═══════════════════════════════════════════════════════════════════════════
    this.commandPatterns.set(
      /(?:set |change )?(?:card )?background (?:color|colour)? (?:to )?(.+)/i,
      (match) => {
        const colorValue = this.parseColorValue(match[1]);
        return colorValue ? { properties: { cardBackgroundColor: colorValue } } : {};
      }
    );

    this.commandPatterns.set(
      /(?:set |change )?(?:card )?border (?:color|colour) (?:to )?(.+)/i,
      (match) => {
        const colorValue = this.parseColorValue(match[1]);
        return colorValue ? { properties: { cardBorderColor: colorValue } } : {};
      }
    );

    this.commandPatterns.set(
      /(?:set |change )?(?:card )?border (?:width |size )?(?:to )?(\d+)/i,
      (match) => ({ properties: { cardBorderWidth: parseInt(match[1], 10) } })
    );

    this.commandPatterns.set(
      /(?:set |change )?(?:card )?(?:border )?radius (?:to )?(\d+)/i,
      (match) => ({ properties: { cardBorderRadius: parseInt(match[1], 10) } })
    );

    this.commandPatterns.set(
      /(?:set |change )?(?:card )?padding (?:to )?(\d+)/i,
      (match) => ({ properties: { cardPadding: parseInt(match[1], 10) } })
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // ICON COMMANDS
    // ═══════════════════════════════════════════════════════════════════════════
    this.commandPatterns.set(
      /(?:set |add |use )?icon (?:to )?["']?([A-Za-z]+)["']?/i,
      (match) => ({ properties: { icon: match[1] } })
    );

    this.commandPatterns.set(
      /(?:remove |hide )?icon/i,
      () => ({ properties: { iconPosition: 'none' } })
    );

    this.commandPatterns.set(
      /(?:put |move |place )?icon (?:on |to )?(?:the )?(left|right|top)/i,
      (match) => {
        const iconPosition = match[1].toLowerCase();
        return isIconPosition(iconPosition) ? { properties: { iconPosition } } : {};
      }
    );

    this.commandPatterns.set(
      /(?:set |change )?icon size (?:to )?(\d+)/i,
      (match) => ({ properties: { iconSize: parseInt(match[1], 10) } })
    );

    this.commandPatterns.set(
      /(?:set |change )?icon (?:color|colour) (?:to )?(.+)/i,
      (match) => {
        const colorValue = this.parseColorValue(match[1]);
        return colorValue ? { properties: { iconColor: colorValue } } : {};
      }
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // LAYOUT COMMANDS
    // ═══════════════════════════════════════════════════════════════════════════
    this.commandPatterns.set(
      /(?:use |set )?horizontal(?: layout)?/i,
      () => ({ properties: { layout: 'horizontal' } })
    );

    this.commandPatterns.set(
      /(?:use |set )?vertical(?: layout)?/i,
      () => ({ properties: { layout: 'vertical' } })
    );

    this.commandPatterns.set(
      /(?:set |change )?gap (?:to )?(\d+)/i,
      (match) => ({ properties: { gap: parseInt(match[1], 10) } })
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // TREND COMMANDS
    // ═══════════════════════════════════════════════════════════════════════════
    this.commandPatterns.set(
      /(?:set |add )?trend (?:to )?([+-]?\d+(?:\.\d+)?)/i,
      (match) => ({ properties: { trend: { value: parseFloat(match[1]) } } })
    );

    this.commandPatterns.set(
      /(?:remove |hide )?trend/i,
      () => ({ properties: { trend: undefined } })
    );

    this.commandPatterns.set(
      /show trend icon/i,
      () => ({ properties: { showTrendIcon: true } })
    );

    this.commandPatterns.set(
      /hide trend icon/i,
      () => ({ properties: { showTrendIcon: false } })
    );
  }

  canHandle(command: string, component: Component): boolean {
    return component.type === ComponentType.DATA_CARD && this.hasDataCardKeywords(command);
  }

  private hasDataCardKeywords(command: string): boolean {
    const keywords = [
      // Data text integration
      "data text", "datatext", "data binding", "static value",
      "value field", "label field", "dynamic label", "empty value",
      // Core properties
      "value", "label", "subtitle", "trend",
      // Formatting
      "format", "currency", "percent", "number", "compact",
      // Typography
      "font", "size", "bold", "color", "colour", "weight",
      // Card styling
      "background", "border", "radius", "padding", "shadow",
      // Icon
      "icon",
      // Layout
      "horizontal", "vertical", "gap", "layout",
      // States
      "loading", "error"
    ];
    const lowerCommand = command.toLowerCase();
    return keywords.some(keyword => lowerCommand.includes(keyword));
  }

  async parseCommand(command: string, component: Component): Promise<CommandResult> {
    // Split complex commands
    const commandParts = command.split(/\s+(?:and|with|also)\s+/i);
    const changes: PropertyChange = { properties: {}, style: {} };

    for (const part of commandParts) {
      // Try pattern matching
      const patternMatch = this.matchPatterns(part, component);
      if (patternMatch) {
        if (patternMatch.properties) {
          changes.properties = { ...changes.properties, ...patternMatch.properties };
        }
        if (patternMatch.style) {
          changes.style = { ...changes.style, ...patternMatch.style };
        }
      }
    }

    // Check if we found any changes
    if (Object.keys(changes.properties || {}).length > 0 ||
        Object.keys(changes.style || {}).length > 0) {

      const validation = this.validateChanges(changes, component);
      if (!validation.valid) {
        return {
          success: false,
          error: validation.error,
          suggestions: await this.getSuggestions(component)
        };
      }

      return {
        success: true,
        command: {
          type: this.determineCommandType(changes),
          changes,
          confidence: this.calculateConfidence(command, changes),
          description: this.generateDescription(changes, component)
        },
        suggestions: await this.getSuggestions(component)
      };
    }

    return {
      success: false,
      error: "Could not understand the data card command",
      suggestions: await this.getSuggestions(component)
    };
  }

  async getExampleCommands(): Promise<string[]> {
    return [
      // Data Text Integration
      'Enable data text mode',
      'Disable data text mode',
      'Use data binding',
      'Use static value',
      'Set value field to "temperature"',
      'Set label field to "sensorName"',
      'Enable dynamic label',
      'Disable dynamic label',
      'Set empty value text to "N/A"',

      // Static values
      'Set value to 1234',
      'Set label to "Temperature"',
      'Set subtitle to "Last 24 hours"',

      // Formatting
      'Format as currency',
      'Format as percent',
      'Format as compact',
      'Format as integer',
      'Format as date',
      'Show 2 decimals',
      'Remove thousands separator',
      'Set currency symbol to €',

      // Typography
      'Set value color to blue',
      'Set value size to 32px',
      'Make value bold',
      'Set label color to gray',
      'Make label bold',

      // Card styling
      'Set background color to white',
      'Set border color to gray',
      'Set border radius to 12',
      'Set padding to 16',

      // Icon
      'Set icon to Thermometer',
      'Put icon on left',
      'Remove icon',

      // Layout
      'Use horizontal layout',
      'Use vertical layout',
      'Set gap to 8',

      // Trend
      'Set trend to +5.2',
      'Remove trend',

      // States
      'Show loading state',
      'Hide error state'
    ];
  }

  async getSuggestions(component: Component): Promise<string[]> {
    const suggestions: string[] = [];
    const props = component.properties || {};

    // Data Text mode suggestion
    if (!props.useDataText) {
      suggestions.push('Enable data text mode');
    } else {
      suggestions.push('Disable data text mode');
      if (!props.valueField) {
        suggestions.push('Set value field to "value"');
      }
      if (!props.useDynamicLabel) {
        suggestions.push('Enable dynamic label');
      }
    }

    // Static value suggestions (when not using data text)
    if (!props.useDataText) {
      if (!props.value && props.value !== 0) {
        suggestions.push('Set value to 1234');
      }
      if (!props.label) {
        suggestions.push('Set label to "Metric"');
      }
    }

    // Formatting suggestions
    const currentFormatter = props.valueFormatter;
    if (currentFormatter !== 'currency') {
      suggestions.push('Format as currency');
    }
    if (currentFormatter !== 'percent') {
      suggestions.push('Format as percent');
    }
    if (currentFormatter !== 'compact') {
      suggestions.push('Format as compact');
    }

    // Icon suggestions
    if (!props.icon) {
      suggestions.push('Set icon to TrendingUp');
    } else {
      const currentPosition = props.iconPosition || 'right';
      if (currentPosition !== 'left') {
        suggestions.push('Put icon on left');
      }
      if (currentPosition !== 'top') {
        suggestions.push('Put icon on top');
      }
    }

    // Layout suggestions
    const currentLayout = props.layout || 'vertical';
    if (currentLayout !== 'horizontal') {
      suggestions.push('Use horizontal layout');
    } else {
      suggestions.push('Use vertical layout');
    }

    // Typography suggestions
    if (!props.valueColor || props.valueColor === '#1F2937') {
      suggestions.push('Set value color to blue');
    }

    return suggestions.slice(0, 10);
  }

  protected override validateChanges(changes: PropertyChange, component: Component): { valid: boolean; error?: string } {
    // Validate valueFormatter
    if (changes.properties?.valueFormatter !== undefined) {
      const validFormatters = new Set(['number', 'currency', 'percent', 'date', 'time', 'datetime', 'compact', 'decimal', 'integer', 'custom']);
      const formatter = changes.properties.valueFormatter;
      if (typeof formatter !== 'string' || !validFormatters.has(formatter)) {
        return { valid: false, error: `Invalid formatter: ${String(formatter)}. Must be one of: ${Array.from(validFormatters).join(', ')}` };
      }
    }

    if (changes.properties?.decimalPlaces !== undefined) {
      const decimals = Number(changes.properties.decimalPlaces);
      if (!Number.isInteger(decimals) || decimals < 0 || decimals > 10) {
        return { valid: false, error: `Invalid decimal places: ${changes.properties.decimalPlaces}. Must be a number between 0 and 10.` };
      }
    }

    // Validate iconPosition
    if (changes.properties?.iconPosition !== undefined) {
      const validPositions = new Set(['left', 'right', 'top', 'none']);
      const iconPosition = changes.properties.iconPosition;
      if (typeof iconPosition !== 'string' || !validPositions.has(iconPosition)) {
        return { valid: false, error: `Invalid icon position: ${String(iconPosition)}. Must be one of: ${Array.from(validPositions).join(', ')}` };
      }
    }

    // Validate layout
    if (changes.properties?.layout !== undefined) {
      const validLayouts = new Set(['horizontal', 'vertical']);
      const layout = changes.properties.layout;
      if (typeof layout !== 'string' || !validLayouts.has(layout)) {
        return { valid: false, error: `Invalid layout: ${String(layout)}. Must be one of: ${Array.from(validLayouts).join(', ')}` };
      }
    }

    // Validate font weights
    if (changes.properties?.valueFontWeight !== undefined) {
      const weight = changes.properties.valueFontWeight;
      if (typeof weight !== 'number' || weight < 100 || weight > 900) {
        return { valid: false, error: `Invalid value font weight: ${weight}. Must be a number between 100 and 900.` };
      }
    }

    if (changes.properties?.labelFontWeight !== undefined) {
      const weight = changes.properties.labelFontWeight;
      if (typeof weight !== 'number' || weight < 100 || weight > 900) {
        return { valid: false, error: `Invalid label font weight: ${weight}. Must be a number between 100 and 900.` };
      }
    }

    // Validate gap
    if (changes.properties?.gap !== undefined) {
      const gap = changes.properties.gap;
      if (typeof gap !== 'number' || gap < 0) {
        return { valid: false, error: `Invalid gap: ${gap}. Must be a non-negative number.` };
      }
    }

    // Validate border values
    if (changes.properties?.cardBorderWidth !== undefined) {
      const width = changes.properties.cardBorderWidth;
      if (typeof width !== 'number' || width < 0) {
        return { valid: false, error: `Invalid border width: ${width}. Must be a non-negative number.` };
      }
    }

    if (changes.properties?.cardBorderRadius !== undefined) {
      const radius = changes.properties.cardBorderRadius;
      if (typeof radius !== 'number' || radius < 0) {
        return { valid: false, error: `Invalid border radius: ${radius}. Must be a non-negative number.` };
      }
    }

    return super.validateChanges(changes, component);
  }
}

export const dataCardCommandHandler = new DataCardCommandHandler();
